import { useEffect, type RefObject } from 'react';

/**
 * Mobile-only chrome side-effects: composer auto-focus on conversation entry,
 * the `--mobile-bottom-bar-height` CSS var tracking the bottom stack, and the
 * body class that floats the chat input above a fullscreen module pane. Pure
 * side-effects — no render output; refs are threaded by reference so they stay
 * shared with ChatBox.
 */
export function useMobileChatChrome({
  isMobile,
  activeConversationId,
  isStreaming,
  isLoading,
  isMobileModuleView,
  isModulePaneVisible,
  chatInputRef,
  bottomStackRef,
}: {
  isMobile: boolean;
  activeConversationId: string | undefined;
  isStreaming: boolean;
  isLoading: boolean;
  isMobileModuleView: boolean;
  isModulePaneVisible: boolean;
  chatInputRef: RefObject<HTMLTextAreaElement | null>;
  bottomStackRef: RefObject<HTMLDivElement | null>;
}): void {
  // Auto-focus textarea on mobile when in chat view (not module view).
  // This also forces WKWebView to evaluate safe-area-inset-top on first load.
  useEffect(() => {
    if (!isMobile || !activeConversationId) return;
    if (isStreaming || isLoading) return;
    if (isMobileModuleView) return;
    const timer = setTimeout(() => chatInputRef.current?.focus(), 150);
    return () => clearTimeout(timer);
  }, [isMobile, activeConversationId, isStreaming, isLoading, isMobileModuleView, chatInputRef]);

  // Track bottom stack height so fullscreen module knows how far up to stop
  useEffect(() => {
    const el = bottomStackRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty('--mobile-bottom-bar-height', `${el.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [bottomStackRef]);

  // Mobile fullscreen module: add body class so chat input floats above it
  useEffect(() => {
    if (isMobile && isModulePaneVisible) {
      document.body.classList.add('mobile-module-fullscreen-active');
    } else {
      document.body.classList.remove('mobile-module-fullscreen-active');
    }
    return () => document.body.classList.remove('mobile-module-fullscreen-active');
  }, [isMobile, isModulePaneVisible]);
}
